import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import { ErrorLayout, Loader, Suspense } from '@axiom/ui';

import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { TalentSearch } from './components/TalentSearch/TalentSearch';
import { MyEngagementsDashboard } from './components/MyEngagements/MyEngagementsDashboard';
import { MyEngagementsItem } from './components/MyEngagements/MyEngagementsItem';
import { EngagementTalent } from './components/EngagementTalent/EngagementTalent';
import { EngagementTalentDetails } from './components/EngagementTalent/EngagementTalentDetails';
import { MyEngagementsItemInterviewTalent } from './components/MyEngagements/MyEngagementsItemInterviewTalent';
import { MyEngagementsItemDetails } from './components/MyEngagements/MyEngagementsItemDetails';
import { SubmissionPdfDownload } from './components/SubmissionPdfDownload/SubmissionPdfDownload';
import { SubmissionPdfData } from './components/SubmissionPdfData/SubmissionPdfData';
import { AccessLegalTalentPage } from './pages/AccessLegalTalentPage';
import { MyAxiomPage } from './pages/MyAxiomPage';
import { BenchView } from './components/BenchView/BenchView';
import { TalentDetailPage } from './pages/TalentDetailPage';
import { MyAxiomLandingPage } from './components/MyAxiomLandingPage/MyAxiomLandingPage';
import { Redirecter } from './components/Redirecter/Redirecter';
import { SplitSubmissionPdfDownload } from './components/SplitSubmissionPdfDownload/SplitSubmissionPdfDownload';
import { SelfServiceRoute } from './routes/SelfServiceRoute';
import { EngagementHeader } from './components/EngagementHeader/EngagementHeader';
import { EngagementPage } from './pages/EngagementPage';
import { PublicEngagement } from './components/PublicEngagement/PublicEngagement';
import { PageLayout } from './layouts/PageLayout/PageLayout';
import { RedirectToBench } from './components/RedirectToBench/RedirectToBench';
import { RedirectToPublicSubmission } from './components/RedirectToPublicSubmission/RedirectToPublicSubmission';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { ClientIntent } from './components/ClientIntent/ClientIntent';
import { DirectEngagements } from './components/DirectEngagements/DirectEngagements';

export const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<PageLayout />}>
        <Route path="" element={<Navigate to="account" />} />
        <Route path="projects">
          <Route index path="" element={<Navigate to="/" replace />} />
          <Route index path="*" element={<Redirecter to="../" />} />
        </Route>
        <Route path="engagements">
          <Route index path="" element={<Navigate to="/" replace />} />
          <Route index path="*" element={<Redirecter to="../" />} />
        </Route>
        <Route path="my-axiom">
          <Route index path="" element={<Navigate to="/" replace />} />
          <Route index path="*" element={<Redirecter to="../" />} />
        </Route>
        <Route path="bench" element={<RedirectToBench />} />
        <Route path="access-legal-talent">
          <Route
            index
            path=""
            element={<Navigate to="../browse-talent" replace />}
          />
          <Route path="*" element={<Redirecter to="/browse-talent" />} />
        </Route>

        <Route path="browse-talent">
          <Route path="page" element={<AccessLegalTalentPage />}>
            <Route path=":pageNumber" element={<TalentSearch />} />
            <Route path="" element={<Navigate to="1" replace />} />
          </Route>
          <Route path="" element={<Navigate to="page" replace />} />
          <Route path=":id" element={<TalentDetailPage />} />
        </Route>
        {/*
      Don't confuse this with 'engagements' <= with an 's'
      this is the public submission aka matches tab
      */}
        <Route
          path="engagement/account/:accountId/submission/:submissionId/matches/:matchesId?"
          element={<RedirectToPublicSubmission />}
        />
        <Route
          path="account/:accountId/submission/:submissionId/engagement"
          element={<EngagementPage />}
        >
          <Route
            path=""
            element={
              <Suspense>
                <PublicEngagement />
              </Suspense>
            }
          >
            <Route
              path=":talentSlug?"
              element={
                <Suspense fallback={<Loader type="bars" />}>
                  <EngagementTalentDetails key="public-engagement-talent" />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route path="account" element={<MyAxiomPage />}>
          <Route path=":accountId" element={<Outlet />}>
            <Route path="" element={<MyAxiomLandingPage />} />
            <Route path="my-bench" element={<BenchView key="my-bench" />}>
              <Route path="" element={<BenchView key="my-bench-index" />} />
            </Route>
            <Route
              path="service-choice"
              element={<Navigate to="../intake" replace />}
            />
            <Route
              path="dashboard"
              element={<Navigate to="../my-engagements" replace />}
            />
            <Route
              path="engagements"
              element={<Navigate to="../my-engagements" replace />}
            />

            <Route path="my-engagements" element={<MyEngagementsDashboard />} />
            <Route path="intake/intro" element={<ClientIntent />} />
            <Route
              path="intake/:submissionId?"
              element={
                <SelfServiceRoute>
                  <DirectEngagements />
                </SelfServiceRoute>
              }
            />
            <Route path="submission">
              <Route
                path=""
                element={<Navigate to="../my-engagements" replace />}
              />
              <Route
                path=":submissionId"
                element={
                  <Suspense>
                    <EngagementHeader />
                    <MyEngagementsItem />
                  </Suspense>
                }
              >
                <Route path="" element={<Navigate to="matches" replace />} />
                <Route path="matches" element={<EngagementTalent />}>
                  <Route
                    path=":talentSlug?"
                    element={
                      <Suspense fallback={<Loader type="bars" />}>
                        <EngagementTalentDetails key="submission-engagement-talent" />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path="interviews"
                  element={
                    <Suspense fallback={<Loader type="bars" />}>
                      <MyEngagementsItemInterviewTalent />
                    </Suspense>
                  }
                />
                <Route
                  path="engagement-details"
                  element={
                    <Suspense fallback={<Loader type="bars" />}>
                      <MyEngagementsItemDetails />
                    </Suspense>
                  }
                />
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<Loader type="bars" />}>
                      <Navigate to="matches" replace />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="account">
          <Route path=":accountId">
            <Route
              path=":submissionId"
              element={
                <Suspense fallback={<Loader type="bars" />}>
                  <Outlet />
                </Suspense>
              }
            >
              <Route path="pdf" element={<SubmissionPdfData />} />
              <Route
                path="pdf/split"
                element={
                  <Suspense>
                    <Outlet />
                  </Suspense>
                }
              >
                <Route path="" element={<Navigate to="./download" replace />} />
                <Route
                  path="download"
                  element={<SplitSubmissionPdfDownload />}
                />
              </Route>
              <Route
                path="pdf/download"
                element={
                  <Suspense>
                    <SubmissionPdfDownload />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Route>

        <Route
          path="400"
          element={
            <ErrorLayout
              errorMessage="There was a problem with your request. Please try something else"
              name="BAD_REQUEST_LAYOUT"
            />
          }
        />
        <Route path="401" element={<UnauthorizedPage />} />
        <Route path="403" element={<ForbiddenPage />} />
        <Route path="404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="../404" replace />} />
      </Route>
    </Routes>
  );
};
